/**
 * Pick up a new service worker promptly. public/sw.js calls skipWaiting()
 * + clients.claim(), so after a deploy the new worker takes over open pages
 * a few seconds after they load ('controllerchange'). The page itself is
 * still running the old bundle, so reload it once — but only when that is
 * invisible to the visitor:
 *   - an update, not the first install (there was a controller at load);
 *   - no user interaction on this page yet: audio can't be playing without
 *     a gesture (autoplay policy), and nothing typed can be lost;
 *   - no <audio>/<video> playing, not an /admin page (belt and braces);
 *   - not reloaded for this reason in the last minute (no loops).
 * Otherwise nothing happens: the new worker already serves navigations and
 * guide data network-first, so the next page load runs the new code.
 */
export const SW_RELOAD_KEY = 'wf-sw-reloaded-at';
export const SW_RELOAD_GUARD_MS = 60 * 1000;

export function shouldReloadForNewServiceWorker({
  hadController, userActive, mediaPlaying, pathname = '/', lastReloadAt = 0, now = Date.now(),
}) {
  if (!hadController || userActive || mediaPlaying) return false;
  if (pathname.startsWith('/admin')) return false;
  if (lastReloadAt && now - lastReloadAt < SW_RELOAD_GUARD_MS) return false;
  return true;
}

function readLastReload() {
  try { return Number(window.sessionStorage.getItem(SW_RELOAD_KEY)) || 0; } catch { return 0; }
}

function writeLastReload(now) {
  try { window.sessionStorage.setItem(SW_RELOAD_KEY, String(now)); } catch { /* private mode */ }
}

export function installReloadOnServiceWorkerUpdate(sw = navigator.serviceWorker) {
  if (!sw) return;
  const hadController = Boolean(sw.controller);
  let interacted = false;
  const mark = () => { interacted = true; };
  ['pointerdown', 'keydown', 'touchstart'].forEach((t) => (
    window.addEventListener(t, mark, { capture: true, once: true, passive: true })
  ));
  let done = false;
  sw.addEventListener('controllerchange', () => {
    if (done) return;
    done = true;
    const now = Date.now();
    const ok = shouldReloadForNewServiceWorker({
      hadController,
      userActive: interacted || Boolean(navigator.userActivation?.hasBeenActive),
      mediaPlaying: Array.from(document.querySelectorAll('audio, video')).some((m) => !m.paused && !m.ended),
      pathname: window.location.pathname,
      lastReloadAt: readLastReload(),
      now,
    });
    if (ok) {
      writeLastReload(now);
      window.location.reload();
    }
  });
}
